// Multi-family: Amenities CRUD page (P1-10). Building binding + optional
// schedule pointer. Local-only banner because controller-door binding
// has not landed yet.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import { CreateAmenityBody, UpdateAmenityBody } from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { CrudTable, fieldCls } from '../components/CrudTable.js';
import { CrudModal, FormField } from '../components/CrudModal.js';
import { RowActions, Toast } from '../components/RowActions.js';
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';

interface AmenityRow {
  id: string;
  name: string;
  description: string | null;
  building_id: string | null;
  schedule_id: string | null;
}

interface BuildingOption {
  id: string;
  name: string;
}

interface ScheduleOption {
  id: string;
  name: string;
}

async function loadAmenities(): Promise<AmenityRow[]> {
  const res = await apiClient.get<{ data: AmenityRow[] }>('/api/v1/amenities');
  return res.data;
}

async function loadBuildings(): Promise<BuildingOption[]> {
  const res = await apiClient.get<{ data: BuildingOption[] }>('/api/v1/buildings');
  return res.data;
}

async function loadSchedules(): Promise<ScheduleOption[]> {
  try {
    const res = await apiClient.get<{ data: ScheduleOption[] }>('/api/v1/schedules');
    return res.data;
  } catch {
    return [];
  }
}

type ModalKind = 'create' | 'edit' | 'delete';
type ModalState = { kind: ModalKind; row: AmenityRow | null };

export function AmenitiesPage() {
  const [rows, setRows] = useState<AmenityRow[] | null>(null);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [schedules, setSchedules] = useState<ScheduleOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    Promise.all([loadAmenities(), loadBuildings(), loadSchedules()])
      .then(([as, bs, ss]) => {
        if (cancelled) return;
        setRows(as);
        setBuildings(bs);
        setSchedules(ss);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const buildingName = (id: string | null) =>
    id ? (buildings.find((b) => b.id === id)?.name ?? id) : '—';
  const scheduleName = (id: string | null) =>
    id ? (schedules.find((s) => s.id === id)?.name ?? 'Custom') : 'Always';

  return (
    <div className="space-y-6">
      {/* flex-wrap + min-w-0: at 320px the icon, title and the action button
          cannot share one row, and the button is what has to move. Same
          header shape as HouseholdsPage. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="fitness_center" className="text-2xl" />
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Amenities
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Shared spaces (gym, pool, lounge). Hardware enforcement lands later.
            </p>
          </div>
        </div>
        <Button onClick={() => setModal({ kind: 'create', row: null })}>
          <Icon name="add" />
          <span>Add amenity</span>
        </Button>
      </header>

      <LocalOnlyBanner
        title="Local records only"
        body="Amenity records do not yet bind to controller doors or schedules. Configuring an amenity here records intent, but does not yet enforce access on hardware."
      />

      {toast ? <Toast message={toast} onDismiss={() => setToast(null)} /> : null}

      <CrudTable<AmenityRow>
        rows={rows}
        error={error}
        emptyMessage="No amenities defined yet. Click Add amenity to create one."
        columns={[
          { key: 'name', header: 'Name', width: '22%' },
          {
            key: 'description',
            header: 'Description',
            render: (a) =>
              a.description ?? <span className="text-on-surface-variant">—</span>,
          },
          {
            key: 'building_id',
            header: 'Building',
            width: '22%',
            render: (a) => buildingName(a.building_id),
          },
          {
            key: 'schedule_id',
            header: 'Schedule',
            width: '14%',
            render: (a) => scheduleName(a.schedule_id),
          },
        ]}
        actions={(a) => (
          <RowActions
            actions={[
              { label: 'Edit', onClick: () => setModal({ kind: 'edit', row: a }) },
              {
                label: 'Delete',
                destructive: true,
                onClick: () => setModal({ kind: 'delete', row: a }),
              },
            ]}
          />
        )}
      />

      {modal ? (
        <AmenityModal
          state={modal}
          buildings={buildings}
          schedules={schedules}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AmenityModal({
  state,
  buildings,
  schedules,
  onCancel,
  onDone,
}: {
  state: ModalState;
  buildings: BuildingOption[];
  schedules: ScheduleOption[];
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { kind, row } = state;
  const { key: idemKey } = useIdempotencyKey();
  const [name, setName] = useState(row?.name ?? '');
  const [description, setDescription] = useState(row?.description ?? '');
  const [buildingId, setBuildingId] = useState(row?.building_id ?? '');
  const [scheduleId, setScheduleId] = useState(row?.schedule_id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'create') {
        const body = CreateAmenityBody.parse({
          name: name.trim(),
          description: description.trim() || null,
          building_id: buildingId || null,
          schedule_id: scheduleId || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .post('/api/v1/amenities', body);
        onDone(`Amenity "${body.name}" created.`);
        return;
      }
      if (kind === 'edit') {
        const body = UpdateAmenityBody.parse({
          name: name.trim(),
          description: description.trim() || null,
          building_id: buildingId || null,
          schedule_id: scheduleId || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/amenities/${row!.id}`, body);
        onDone(`Amenity "${name.trim()}" updated.`);
        return;
      }
      if (kind === 'delete') {
        await apiClient.delete(`/api/v1/amenities/${row!.id}`);
        onDone(`Amenity "${row!.name}" deleted.`);
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
      setSubmitting(false);
    }
  }

  const title =
    kind === 'create'
      ? 'Add amenity'
      : kind === 'edit'
        ? `Edit ${row?.name}`
        : `Delete ${row?.name}`;
  const tone: 'default' | 'destructive' = kind === 'delete' ? 'destructive' : 'default';
  const icon = kind === 'delete' ? 'delete' : kind === 'edit' ? 'edit' : 'add';
  const submitLabel = kind === 'delete' ? 'Delete' : kind === 'edit' ? 'Save' : 'Create';

  return (
    <CrudModal
      title={title}
      icon={icon}
      tone={tone}
      submitting={submitting}
      error={error}
      submitLabel={submitLabel}
      canSubmit={kind === 'delete' || name.trim().length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      {kind === 'delete' ? (
        <p className="font-body-md text-body-md text-on-surface">
          Deleting <strong>{row?.name}</strong> removes it from the amenities
          directory. Resident bookings on this amenity will fail to resolve.
        </p>
      ) : (
        <>
          <FormField id="amenity-name" label="Name">
            <input
              id="amenity-name"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldCls}
            />
          </FormField>
          <FormField id="amenity-description" label="Description" hint="Optional.">
            <textarea
              id="amenity-description"
              maxLength={2000}
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={fieldCls}
            />
          </FormField>
          <FormField id="amenity-building" label="Building" hint="Optional.">
            <select
              id="amenity-building"
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
              className={fieldCls}
            >
              <option value="">— Property-wide —</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            id="amenity-schedule"
            label="Schedule"
            hint="Optional. Leave blank for always-open."
          >
            <select
              id="amenity-schedule"
              value={scheduleId}
              onChange={(e) => setScheduleId(e.target.value)}
              className={fieldCls}
            >
              <option value="">— Always —</option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
        </>
      )}
    </CrudModal>
  );
}
